/**
 * GoBingeBox — Subscription pricing & tier configuration.
 *
 * SINGLE SOURCE OF TRUTH for all pricing math. Imported by BOTH the web app
 * (apps/web) and the API (apps/api). Never duplicate prices anywhere else.
 *
 * All money is represented in integer cents to avoid floating-point drift.
 * Every value here is intended to be owner-editable.
 *
 * See spec §9 (Subscription tiers & billing).
 */

export type TierId = 'individual' | 'bingebox' | 'bingebox_plus';
export type BillingPeriod = 'monthly' | 'annual';

export interface TierFeatures {
  /** Full episodes (not just previews) — true for every paid tier. */
  fullEpisodes: boolean;
  /** Ad-free playback. Individual is ad-supported. */
  adFree: boolean;
  /** Merch store discount as a fraction (0.05 = 5%). */
  merchDiscountPct: number;
  /** Offline downloads — ships later, flag drives UI now. */
  offlineDownloads: boolean;
  /** Games access — when games exist. */
  gamesAccess: boolean;
  /** BingeBox+ hook: 4K / highest available quality. */
  highestQuality: boolean;
  /** BingeBox+ hook: early access to new episode drops. */
  earlyAccess: boolean;
}

export interface TierConfig {
  id: TierId;
  /** Display name. */
  name: string;
  /** Short marketing tagline. */
  tagline: string;
  /** Price for 1 stream, billed monthly (cents). */
  baseMonthlyCents: number;
  features: TierFeatures;
}

/**
 * Global, non-tier-specific pricing knobs.
 */
export const PRICING = {
  currency: 'usd',
  /** Each additional simultaneous stream: +$5.00 / month. */
  extraStreamMonthlyCents: 500,
  /** Streams included in the base price of every tier. */
  includedStreams: 1,
  /** Hard cap on simultaneous streams per subscription. */
  maxStreams: 8,
  /**
   * Annual discount applied to (monthly total × 12).
   * ~17% ≈ "2 months free". Owner editable.
   */
  annualDiscount: 0.17,
} as const;

export const TIERS: Record<TierId, TierConfig> = {
  individual: {
    id: 'individual',
    name: 'Individual',
    tagline: 'Everything, with ads.',
    baseMonthlyCents: 599,
    features: {
      fullEpisodes: true,
      adFree: false,
      merchDiscountPct: 0.05,
      offlineDownloads: false,
      gamesAccess: false,
      highestQuality: false,
      earlyAccess: false,
    },
  },
  bingebox: {
    id: 'bingebox',
    name: 'BingeBox',
    tagline: 'Ad-free streaming with downloads.',
    baseMonthlyCents: 999,
    features: {
      fullEpisodes: true,
      adFree: true,
      merchDiscountPct: 0.1,
      offlineDownloads: true,
      gamesAccess: true,
      highestQuality: false,
      earlyAccess: false,
    },
  },
  bingebox_plus: {
    id: 'bingebox_plus',
    name: 'BingeBox+',
    tagline: '4K, early drops, and the biggest merch perks.',
    baseMonthlyCents: 1999,
    features: {
      fullEpisodes: true,
      adFree: true,
      merchDiscountPct: 0.15,
      offlineDownloads: true,
      gamesAccess: true,
      // BingeBox+ differentiation hook (spec §9 note): 4K + early access.
      highestQuality: true,
      earlyAccess: true,
    },
  },
};

/** Ordered list, cheapest → most expensive (for rendering plan cards). */
export const TIER_ORDER: TierId[] = ['individual', 'bingebox', 'bingebox_plus'];

export function isTierId(value: string): value is TierId {
  return value === 'individual' || value === 'bingebox' || value === 'bingebox_plus';
}

export function getTier(id: TierId): TierConfig {
  return TIERS[id];
}

/** Clamp a requested stream count into the allowed [1, maxStreams] range. */
export function clampStreams(streams: number): number {
  const n = Math.floor(Number.isFinite(streams) ? streams : PRICING.includedStreams);
  return Math.min(PRICING.maxStreams, Math.max(PRICING.includedStreams, n));
}

/** Monthly recurring total for a tier at a given stream count (cents). */
export function monthlyTotalCents(tierId: TierId, streams: number): number {
  const tier = getTier(tierId);
  const count = clampStreams(streams);
  const extraStreams = count - PRICING.includedStreams;
  return tier.baseMonthlyCents + extraStreams * PRICING.extraStreamMonthlyCents;
}

/**
 * Annual one-time total (cents): monthly total × 12 × (1 − annualDiscount),
 * rounded to the nearest cent. Annual plans do NOT auto-renew (spec §9).
 */
export function annualTotalCents(tierId: TierId, streams: number): number {
  const monthly = monthlyTotalCents(tierId, streams);
  return Math.round(monthly * 12 * (1 - PRICING.annualDiscount));
}

/** Total price for the selected billing period (cents). */
export function priceForPeriodCents(
  tierId: TierId,
  streams: number,
  period: BillingPeriod,
): number {
  return period === 'annual'
    ? annualTotalCents(tierId, streams)
    : monthlyTotalCents(tierId, streams);
}

/** Merch discount fraction for a tier (0.05 = 5%). */
export function merchDiscountPct(tierId: TierId): number {
  return getTier(tierId).features.merchDiscountPct;
}

/** Format integer cents as a localized USD string, e.g. 2499 -> "$24.99". */
export function formatPriceCents(cents: number, currency = PRICING.currency): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}
